import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import styles from './index.module.less';
import { Button, Dropdown, Menu, message, Modal, Popover, Select } from 'antd';
import { FileType, joyspacePermissionType } from '../../types';
import DownOutlined from '@ant-design/icons/lib/icons/DownOutlined';
import {
  batchPermission,
  batchRemove,
  ShareListType,
} from '../../api/permission';
import { pageMenus, folderMenus } from './config';

interface IProps {
  setCheckboxList: Function;
  checkboxList: string[];
  membersList: any;
  events: any;
  type: FileType;
  id?: string; // webapp中要传id，pwa、hub不需要
  isWeb?: boolean;
}

export default function TabListBottom(props: IProps) {
  const {
    setCheckboxList,
    membersList,
    checkboxList,
    events,
    type,
    id = '',
    isWeb = false,
  } = props;
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isWeb && !events?.listeners['addMembers:successCallback']) {
      events?.on('addMembers:successCallback', setSuccessCallback);
      events?.on('addMembers:setFailCallback', setFailCallback);
    }
    return () => {
      let key = events?.remove ? 'remove' : 'off';
      if (!isWeb && events && events[key]) {
        events[key]('addMembers:successCallback', setSuccessCallback);
        events[key]('addMembers:setFailCallback', setFailCallback);
      }
    };
  }, [events]);

  const init = useCallback(() => {
    setCheckboxList([]);
    const _membersList = membersList.map((item: any) => {
      if (item.isChecked) {
        item.isChecked = false;
      }
      return item;
    });
    events.emit('addmembers:setMembersList', _membersList);
  }, [membersList, events, setCheckboxList]);

  const getPermissionContent = (key: joyspacePermissionType) => {
    switch (key) {
      case joyspacePermissionType.ReadAll:
        return '确定批量设置为“ 可管理 ”吗？';
      case joyspacePermissionType.ReadWrite:
        return '确定批量设置为“ 可编辑 ”吗？';
      case joyspacePermissionType.ReadOnly:
        return '确定批量设置为“ 仅阅读 ”吗？';
      case joyspacePermissionType.ReadDel:
        const loginUser = membersList.find((item: any) => item.isLoginUser);
        if (checkboxList.length === 1 && checkboxList.includes(loginUser?.id)) {
          return '确定删除自己的个人权限吗？';
        }
        if (checkboxList.length > 1 && checkboxList.includes(loginUser?.id)) {
          return '确定删除您自己和其他协作者吗？';
        }
        return '确定批量删除协作者吗？';
      default:
        return '';
    }
  };

  const getMessageContent = (key: joyspacePermissionType) => {
    switch (key) {
      case joyspacePermissionType.ReadAll:
        return '已全部设为可管理';
      case joyspacePermissionType.ReadWrite:
        return '已全部设为可编辑';
      case joyspacePermissionType.ReadOnly:
        return '已全部设为仅阅读';
      case joyspacePermissionType.ReadDel:
        return '已全部删除';
      default:
        return '';
    }
  };

  const setSuccessCallback = (permission: joyspacePermissionType) => {
    message.success(getMessageContent(permission));
    if (id) {
      events.emit('joyspace:updata', {
        pageId: id,
        type,
      });
    } else {
      events.emit('joyspace:updata');
    }
    init();
  };

  const setFailCallback = () => {
    setLoading(false);
  };

  const handleSetPermission = (params: any) => {
    const { permission, shareList = [], shareIdList = [] } = params;
    if (!isWeb) {
      events.emit('addmembers:setBatchPermission', params);
    } else {
      events.emit('addmembers:setStopLoadMore');
      if (permission !== joyspacePermissionType.ReadDel) {
        batchPermission(shareList)
          .then((res: any) => {
            if (!res) {
              return;
            }
            if (res) {
              setSuccessCallback(permission);
            }
          })
          .finally(() => {
            setLoading(false);
            events.emit('addmembers:setOnBottomLoading', false);
          });
      } else if (permission === joyspacePermissionType.ReadDel) {
        batchRemove(shareIdList, props.id)
          .then(res => {
            if (!res) {
              return;
            }
            if (res) {
              setSuccessCallback(permission);
            }
          })
          .finally(() => {
            setLoading(false);
            events.emit('addmembers:setOnBottomLoading', false);
          });
      }
    }
  };

  const onOk = (permission: joyspacePermissionType) => {
    setLoading(true);
    let shareList: ShareListType[] = [];
    let shareIdList: string[] = [];
    checkboxList.map(item => {
      shareList.push({
        permission: permission,
        sendMessage: true,
        shareId: item,
        resourceId: props.id,
      });
      shareIdList.push(item);
    });
    handleSetPermission({
      permission,
      shareList,
      shareIdList,
    });
  };

  const handleMenuClick = (key: joyspacePermissionType) => {
    Modal.confirm({
      title: '权限变更',
      content: getPermissionContent(key),
      okText: '确定',
      cancelText: '取消',
      onOk: () => onOk(key),
    });
  };

  const handleIconRotate = () => {
    const batchIcon = document.querySelector(`.${styles['batch-button-icon']}`);
    batchIcon?.classList.toggle(styles['icon-rotate']);
  };

  const getMenu = () => {
    const menus = type === FileType.Folder ? folderMenus : pageMenus;
    return (
      <Menu
        onClick={({ key }) => {
          handleIconRotate();
          handleMenuClick(Number(key));
        }}
      >
        {menus.map(item => {
          return (
            <Menu.Item key={item.value}>
              <div className={styles['permission-item']}>
                <span className={styles['permission-title']}>{item.label}</span>
                <p className={styles['permission-tip']}>{item.tip}</p>
              </div>
            </Menu.Item>
          );
        })}
      </Menu>
    );
  };

  return (
    <div className={styles['joyspace-tab-list-bottom']}>
      <div className={styles['list-bottom-left']}>
        <span>已选：{checkboxList.length}</span>
      </div>
      <div className={styles['list-bottom-right']}>
        <Button className={styles['bottom-cancel-button']} onClick={init}>
          取消
        </Button>
        <Dropdown
          overlay={getMenu}
          trigger={['click']}
          placement="topRight"
          overlayClassName="batch-button-dropdown"
          onVisibleChange={() => {
            handleIconRotate();
          }}
          getPopupContainer={(triggerNode: any) => triggerNode.parentNode}
        >
          <Button
            onClick={() => {
              setPopoverVisible(!popoverVisible);
            }}
            className={styles['bottom-batch-button']}
            type="primary"
            loading={loading}
          >
            批量设置权限
            <DownOutlined className={styles['batch-button-icon']} />
          </Button>
        </Dropdown>
      </div>
    </div>
  );
}
